import { getDefinition } from "./catalog";
import { isJunction } from "./node-kind";
import type {
  Category,
  ComponentDefinition,
  ComponentNode,
  System,
  SystemNode,
} from "./types";

/** The control arm's free equipment count — also what every public surface
 * (agent tools, draft import) quotes, since those never see an assignment. */
export const FREE_EQUIPMENT_LIMIT = 10;

/** Served offer arms. `control` and `cap5` keep the current paid products and
 * differ only in the free count. Historical `cap6` remains six;
 * `unlimited` is the Installation Pack treatment.
 * Declared here, not in the experiment store, so the edge bundle that imports
 * this policy never pulls a browser module along for a type. */
export const FREE_BUILD_VARIANTS = ["control", "cap5", "cap6", "unlimited"] as const;
export type EquipmentCapVariant = (typeof FREE_BUILD_VARIANTS)[number];

/** Free counted equipment per system for a served offer variant. Lower caps
 * retain the same paid products; `unlimited` is the Installation Pack
 * arm, where no cap applies. An absent assignment reads as control. */
export function freeEquipmentLimit(variant: EquipmentCapVariant | null | undefined = "control"): number {
  switch (variant) {
    case "cap5":
      return 5;
    case "cap6":
      return 6;
    case "unlimited":
      return Infinity;
    default:
      return FREE_EQUIPMENT_LIMIT;
  }
}
export const EQUIPMENT_COUNT_POLICY_VERSION = "equipment-v1" as const;
export const EQUIPMENT_LIMIT_MILESTONES = [5, 8, 10] as const;

export type EquipmentMutationSource =
  | "palette_click"
  | "mobile_palette"
  | "palette_drag"
  | "custom_component"
  | "ai_component"
  | "component_arrival"
  | "starter_template"
  | "resource_example"
  | "duplicate"
  | "paste"
  | "replace"
  | "customize"
  | "revert"
  | "ai_edit"
  | "ai_system"
  | "undo"
  | "redo"
  | "unknown";

export type EquipmentLimitOperation =
  | "add"
  | "remove"
  | "duplicate"
  | "paste"
  | "replace"
  | "generate"
  | "undo"
  | "redo";

export type EquipmentExemption = "fuse" | "busbar" | "layout";

export type EquipmentLimitAttempt = {
  checkoutAttemptId: string;
  mutationId: string;
  source: EquipmentMutationSource;
  operation: EquipmentLimitOperation;
  currentCount: number;
  attemptedDelta: number;
  wouldBeCount: number;
  attemptedDefinitionId: string | null;
  attemptedCategory: Category | "unknown";
  createdAt: number;
};

export type EquipmentCountSummary = {
  equipment: number;
  freeFuses: number;
  freeBusbars: number;
  layoutNodes: number;
};

/** A customised catalog fuse or busbar keeps its reviewed catalog policy. A
 * scratch-built custom part cannot grant itself an exemption. */
function trustedCatalogDefinition(
  node: ComponentNode,
  resolved: ComponentDefinition | undefined,
): ComponentDefinition | undefined {
  if (node.definitionId !== "custom") return resolved;
  const originId = node.customDefinition?.id;
  return originId && originId !== "custom" ? getDefinition(originId) : undefined;
}

export function equipmentNodeExemption(
  node: SystemNode,
): EquipmentExemption | null {
  if (node.kind === "zone") return "layout";
  const resolved = getDefinition(node.definitionId, node.customDefinition);
  if (isJunction(node, resolved)) return "layout";
  const trusted = trustedCatalogDefinition(node, resolved);
  const exemption = trusted?.equipmentLimitExemption;
  if (!exemption || !resolved || !trusted) return null;

  // Customising ratings, labels or terminals does not make safety hardware
  // paid. Changing its product category does: that prevents a catalog busbar
  // or fuse holder from becoming a reusable free shell for arbitrary gear.
  if (resolved.category !== trusted.category) return null;
  if (exemption === "fuse" && (!trusted.isFuseBlock || !resolved.isFuseBlock)) {
    return null;
  }
  return exemption;
}

export function isCountedEquipmentNode(node: SystemNode): boolean {
  return node.kind === "component" && equipmentNodeExemption(node) === null;
}

export function summarizeEquipment(nodes: SystemNode[]): EquipmentCountSummary {
  const summary: EquipmentCountSummary = {
    equipment: 0,
    freeFuses: 0,
    freeBusbars: 0,
    layoutNodes: 0,
  };
  for (const node of nodes) {
    const exemption = equipmentNodeExemption(node);
    if (exemption === "fuse") summary.freeFuses += 1;
    else if (exemption === "busbar") summary.freeBusbars += 1;
    else if (exemption === "layout") summary.layoutNodes += 1;
    else summary.equipment += 1;
  }
  return summary;
}

export function countEquipment(system: Pick<System, "nodes">): number {
  return summarizeEquipment(system.nodes).equipment;
}

/** Grandfathers an existing over-limit drawing while preventing its counted
 * equipment total from increasing further. */
export function equipmentChangeExceedsLimit(
  currentCount: number,
  nextCount: number,
  limit = FREE_EQUIPMENT_LIMIT,
): boolean {
  return nextCount > Math.max(limit, currentCount);
}

export function crossedEquipmentMilestones(
  previousCount: number,
  nextCount: number,
): Array<(typeof EQUIPMENT_LIMIT_MILESTONES)[number]> {
  if (nextCount <= previousCount) return [];
  return EQUIPMENT_LIMIT_MILESTONES.filter(
    (milestone) => previousCount < milestone && nextCount >= milestone,
  );
}
